// Past trips (B10 first half; mockups to come): the family's other trips, and for one of them the
// places this person found, the stickers from its plays and the prizes won, without switching the
// family to it. Needs signal once to list the trips and fetch a past trip's pack (then cached).
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { openOverlay } from '../app/router'
import { Sheet } from '../app/Sheet'
import { api } from '../data/api'
import { db } from '../data/db'
import { packFor, usePack } from '../data/pack'
import { pastTrip } from '../domain/pastTrips'
import { t } from '../strings/en'

export interface ApiTrip {
  id: string
  title: string
  active: boolean
  app?: { kit: string; startDate: string; endDate: string; timezone: string }
}

/** The family's trips (made ones and the built-in packs); `enabled` false skips the fetch (B10's Crate shelf). */
export function useTrips(enabled = true): ApiTrip[] | null {
  const [trips, setTrips] = useState<ApiTrip[] | null>(null)
  useEffect(() => {
    if (!enabled) return
    void Promise.all([api<{ packs: { id: string; title: string }[] }>('/packs/index.json').catch(() => ({ packs: [] })), api<ApiTrip[]>('/api/trips').catch(() => [] as ApiTrip[])]).then(([index, made]) => {
      const all = [...made]
      for (const p of index.packs) if (!all.some((x) => x.id === p.id)) all.push({ id: p.id, title: p.title, active: false })
      setTrips(all)
    })
  }, [enabled])
  return trips
}

export function PastTripsSheet({ person }: { person: Person }) {
  const active = usePack((s) => s.pack?.meta.id)
  const trips = useTrips()
  const others = (trips ?? []).filter((x) => x.id !== active)
  return (
    <Sheet title={t.past.title}>
      {/* B10: this trip's book (a recap so far; the whole story once it's over). */}
      {active ? (
        <button className="btn y press trip-book-open" onClick={() => openOverlay(`trip-book:${active}`)}>
          {t.book.thisTrip}
        </button>
      ) : null}
      <p className="mono sheet-sub">{t.past.sub}</p>
      <div className="panel sheet-panel">
        {trips === null ? (
          <div className="row">
            <small>{t.loading}</small>
          </div>
        ) : others.length ? (
          others.map((x) => (
            <button key={x.id} className="row row-btn press" onClick={() => openOverlay(`past-trip:${x.id}`)}>
              <span className="fam-av">🧳</span>
              <b>{x.title}</b>
              <span className="chev">›</span>
            </button>
          ))
        ) : (
          <div className="row">
            <small>{t.past.none}</small>
          </div>
        )}
      </div>
      <p className="caption">{t.past.note(person.name)}</p>
    </Sheet>
  )
}

export function PastTripSheet({ person, tripId }: { person: Person; tripId: string }) {
  const trips = useTrips()
  const trip = trips?.find((x) => x.id === tripId) ?? null
  const first = usePack((s) => s.firstTrip) ?? tripId
  const events = useLiveQuery(() => db.events.toArray(), [], [])
  const [pack, setPack] = useState<Pack | null>(null)
  useEffect(() => {
    if (trip) void packFor(trip).then(setPack, () => setPack(null))
  }, [trip])
  const s = useMemo(() => pastTrip(events, tripId, first, person.id, pack), [events, tripId, first, person.id, pack])
  return (
    <Sheet title={trip?.title ?? t.past.title}>
      <button className="btn y press trip-book-open" onClick={() => openOverlay(`trip-book:${tripId}`)}>
        {t.book.open}
      </button>
      <p className="mono sheet-sub">{t.past.found(s.found.length)}</p>
      <div className="panel sheet-panel">
        {s.found.length ? s.found.map((p) => (
          <div className="row" key={p.id}>
            <span className="fam-av">📍</span>
            <b>{p.name}</b>
          </div>
        )) : (
          <div className="row">
            <small>{t.past.noFinds}</small>
          </div>
        )}
      </div>
      <p className="mono sheet-sub">{t.past.stickers(s.stickers)}</p>
      <p className="mono sheet-sub">{t.past.prizes(s.prizes.length)}</p>
      {s.prizes.length ? (
        <div className="panel sheet-panel">
          {s.prizes.map((p, i) => (
            <div className="row" key={`${p.title}-${i}`}>
              <span className="fam-av">{p.emoji}</span>
              <b>{p.title}</b>
            </div>
          ))}
        </div>
      ) : null}
    </Sheet>
  )
}
